import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Landmark, Users, Scale, Boxes, Ship, Factory, MapPin, CheckCircle2, ShoppingBag } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const schemes = [
  {
    id: 'rips-2024',
    name: 'RIPS 2024',
    fullName: 'Rajasthan Investment Promotion Scheme',
    icon: <Landmark className="w-8 h-8" />,
    color: 'from-blue-500 to-blue-600',
    highlights: ['Capital Subsidy up to 25%', '75% SGST Reimbursement', 'Interest Subsidy up to 7%'],
    description: 'Comprehensive investment promotion with capital, interest, and tax incentives for manufacturing, services, and MSMEs.',
    validTill: 'March 2029'
  },
  {
    id: 'pmegp',
    name: 'PMEGP',
    fullName: 'Prime Minister Employment Generation Programme',
    icon: <Users className="w-8 h-8" />,
    color: 'from-green-500 to-green-600',
    highlights: ['Up to 35% Subsidy', 'Projects up to ₹50 Lakh', 'Manufacturing & Service'],
    description: 'Central government scheme for self-employment through new micro-enterprises in non-farm sectors.',
    validTill: '2025-26'
  },
  {
    id: 'vyupy',
    name: 'VYUPY',
    fullName: 'Vishwakarma Yuva Udyami Protsahan Yojana',
    icon: <Scale className="w-8 h-8" />,
    color: 'from-orange-500 to-orange-600',
    highlights: ['Up to 8% Interest Subsidy', 'Margin Money 25%', 'Youth 18-45 years'],
    description: 'Flagship scheme promoting youth entrepreneurship with low-cost finance and margin money support.',
    validTill: 'March 2029'
  },
  {
    id: 'msme-policy-2024',
    name: 'MSME Policy 2024',
    fullName: 'Rajasthan MSME Policy Key Incentives',
    icon: <Boxes className="w-8 h-8" />,
    color: 'from-teal-500 to-teal-600',
    highlights: ['Additional Interest Subsidy', 'CGTMSE Fee Reimbursement', 'SME Listing Support'],
    description: 'Comprehensive MSME support with interest subsidy, technology acquisition, and market development assistance.',
    validTill: 'March 2029'
  },
  {
    id: 'export-policy-2024',
    name: 'Export Policy 2024',
    fullName: 'Rajasthan Export Promotion Policy',
    icon: <Ship className="w-8 h-8" />,
    color: 'from-indigo-500 to-indigo-600',
    highlights: ['Freight Subsidy', 'Trade Fair Support up to 75%', 'Certification Reimbursement'],
    description: 'Export-focused incentives including logistics support, compliance cost reimbursement, and global market access.',
    validTill: 'March 2029'
  },
  {
    id: 'logistics-policy-2025',
    name: 'Logistics Policy 2025',
    fullName: 'Rajasthan Logistics Policy',
    icon: <Factory className="w-8 h-8" />,
    color: 'from-cyan-500 to-cyan-600',
    highlights: ['25% Capital Subsidy', '7% Interest Subvention', 'Green Logistics Incentives'],
    description: 'Infrastructure development support for warehouses, cold storage, ICDs, and logistics parks.',
    validTill: 'March 2029'
  },
  {
    id: 'odop-policy-2024',
    name: 'ODOP Policy 2024',
    fullName: 'One District One Product Policy',
    icon: <MapPin className="w-8 h-8" />,
    color: 'from-rose-500 to-rose-600',
    highlights: ['Margin Money up to ₹20L', 'Technology Support 50%', 'Marketing Assistance'],
    description: 'District-specific support for flagship products with margin money, technology, and market access assistance.',
    validTill: 'March 2029'
  },
  {
    id: 'trade-policy',
    name: 'Trade Promotion Policy 2025',
    fullName: 'Rajasthan Trade Promotion Policy',
    icon: <ShoppingBag className="w-8 h-8" />,
    color: 'from-amber-500 to-amber-600',
    highlights: ['6% Interest Subsidy up to ₹1 Cr', '4% for ₹1 Cr – ₹2 Cr', 'Valid till March 2029'],
    description: 'State policy strengthening retail and wholesale trade through credit access, digital adoption, and ease of doing business.',
    validTill: 'March 2029',
    link: '/trade-policy'
  },
];

export const SubsidiesGrid = () => (
  <section className="py-24" data-testid="schemes-grid">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-8">
        {schemes.map((scheme, index) => (
          <motion.div
            key={scheme.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Link to={scheme.link || `/subsidies/${scheme.id}`} className="block h-full">
              <Card className="h-full service-card border border-slate-100 hover:border-[#00bcd4]/30 bg-white overflow-hidden group">
                <CardContent className="p-0">
                  <div className={`h-2 bg-gradient-to-r ${scheme.color}`}></div>
                  <div className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${scheme.color} flex items-center justify-center text-white flex-shrink-0`}>
                        {scheme.icon}
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-semibold text-[#0f172a] group-hover:text-[#3b5998] transition-colors">
                          {scheme.name}
                        </h3>
                        <p className="text-sm text-[#64748b]">{scheme.fullName}</p>
                      </div>
                    </div>
                    
                    <p className="text-[#64748b] text-sm leading-relaxed mb-4">
                      {scheme.description}
                    </p>

                    <div className="space-y-2 mb-4">
                      {scheme.highlights.map((highlight, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-[#00bcd4] flex-shrink-0" />
                          <span className="text-[#0f172a]">{highlight}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-xs text-[#64748b]">Valid till: {scheme.validTill}</span>
                      <span className="inline-flex items-center gap-1 text-[#3b5998] font-medium text-sm group-hover:text-[#00bcd4] transition-colors">
                        Learn More
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
